import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ShoppingBag, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnouncementBar } from './AnnouncementBar';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Logo } from './Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
export function Navbar() {
  const {
    t
  } = useLanguage();
  const {
    totalItems
  } = useCart();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);
  const navLinks = [{
    name: t.nav.cacao,
    to: '/#cacao'
  }, {
    name: t.nav.creations,
    to: '/#creations'
  }, {
    name: t.nav.ingredients,
    to: '/#ingredients'
  }, {
    name: t.nav.giftCard,
    to: '/#carte-cadeau'
  }, {
    name: t.nav.recipes,
    to: '/recettes'
  }, {
    name: t.nav.events,
    to: '/evenements'
  }, {
    name: t.nav.contact,
    to: '/contact'
  }];
  return <header className="fixed top-0 left-0 right-0 z-50" data-id="element-146" data-mp="3n3dsyklhr6z4">
      <AnnouncementBar data-id="element-147" data-mp="3n4xeapm0887r" />

      <div className={`border-b transition-all duration-500 ${isScrolled ? 'border-creme-dark bg-creme/95 py-2.5 shadow-sm backdrop-blur-md' : 'border-transparent bg-creme py-4'}`} data-id="element-148" data-mp="3n1jb5f5inuz9">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 md:px-12" data-id="element-149" data-mp="5n4c355pr4hf">
          <Link to="/" aria-label="Les Artisans Sucrés" data-id="element-150" data-mp="34do1o7gxt3by">
            <Logo data-id="element-151" data-mp="34b1tq6q4wlt7" />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" data-id="element-152" data-mp="39b60h518q268">
            {navLinks.map(link => <Link key={link.to} to={link.to} className="font-sans text-[11px] uppercase tracking-[0.13em] text-cacao-800 transition-colors hover:text-caramel" data-id="element-153" data-mp="31ai0fb49hcec">
                {link.name}
              </Link>)}

            <span aria-hidden="true" className="h-4 w-px bg-caramel/30" data-id="element-154" data-mp="361giog8t8kk8" />
            <LanguageSwitcher data-id="element-155" data-mp="2icfex6ax9xla" />

            <Link to="/panier" className="relative text-cacao-800 transition-colors hover:text-caramel" aria-label={t.nav.cart} data-id="element-156" data-mp="2ibm6m0wbf011">
              <ShoppingBag size={19} strokeWidth={1.5} data-id="element-157" data-mp="2sb4ma556zzna" />
              {totalItems > 0 && <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-caramel px-1 font-sans text-[10px] font-bold text-creme" data-id="element-158" data-mp="2nbs3bvhfzcpz">
                  {totalItems}
                </span>}
            </Link>

            <Link to="/contact" className="border border-cacao-900 px-5 py-2 font-sans text-[11px] uppercase tracking-[0.13em] text-cacao-900 transition-colors duration-300 hover:bg-cacao-900 hover:text-creme" data-id="element-159" data-mp="2ndod772cej4">
              {t.nav.order}
            </Link>
          </nav>

          <div className="flex items-center gap-4 xl:hidden" data-id="element-160" data-mp="55pj2xtrq1bf">
            <LanguageSwitcher data-id="element-161" data-mp="567mqfirfhtd" />
            <Link to="/panier" className="relative text-cacao-900" aria-label={t.nav.cart} data-id="element-162" data-mp="57ipqd4dsljf">
              <ShoppingBag size={22} strokeWidth={1.5} data-id="element-163" data-mp="xtbma4z333xr" />
              {totalItems > 0 && <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-caramel px-1 font-sans text-[10px] font-bold text-creme" data-id="element-164" data-mp="k1x2hvxcqypn">
                  {totalItems}
                </span>}
            </Link>
            <button type="button" aria-label="Menu" aria-expanded={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(open => !open)} className="text-cacao-900" data-id="element-165" data-mp="1gkuo48hdgby7">
              {isMobileMenuOpen ? <X size={26} strokeWidth={1.5} data-id="element-166" data-mp="3k56wqhanvuo8" /> : <Menu size={26} strokeWidth={1.5} data-id="element-167" data-mp="3f49g495av8ou" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-168" data-mp="3b4iipml9pubc">
        {isMobileMenuOpen && <motion.nav initial={{
        opacity: 0,
        y: -12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} transition={{
        duration: 0.25
      }} className="max-h-[70vh] overflow-y-auto border-t border-creme-dark bg-creme shadow-lg xl:hidden" data-id="element-169" data-mp="blpnh5ipm0ti">
            <div className="flex flex-col px-6 py-6" data-id="element-170" data-mp="3u0rto10vi4r4">
              <Link to="/" className="border-b border-creme-dark py-4 font-serif text-lg text-cacao-900 transition-colors hover:text-caramel" data-id="element-171" data-mp="ue214oetcoiy">
                {t.nav.home}
              </Link>
              {navLinks.map(link => <Link key={link.to} to={link.to} className="border-b border-creme-dark py-4 font-serif text-lg text-cacao-900 transition-colors hover:text-caramel" data-id="element-172" data-mp="2d8d712vrd6lu">
                  {link.name}
                </Link>)}
              <Link to="/panier" className="border-b border-creme-dark py-4 font-serif text-lg text-cacao-900 transition-colors hover:text-caramel" data-id="element-173" data-mp="2r8nwq2lc0f5f">
                {t.nav.cart}
                {totalItems > 0 ? ` (${totalItems})` : ''}
              </Link>
              <Link to="/contact" className="mt-6 bg-cacao-900 px-6 py-3 text-center font-sans text-xs uppercase tracking-[0.16em] text-creme" data-id="element-174" data-mp="350casbf55dp1">
                {t.nav.order}
              </Link>
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}